import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/UI/Buttons/Button/Button";
import AccessibleDialog from "../../components/UI/Modals/AccessibleDialog/AccessibleDialog";
import AssociationSwitcher from "../../components/AssociationSwitcher/AssociationSwitcher";
import { selectTokenUserId } from "../../store/auth-slice";
import {
  selectAssociationContextState,
  selectSelectedAssociation,
} from "../../store/associationContext-slice";
import { fetchMyAssociations } from "../../store/associationContext-actions";
import {
  addAdvisorToAssociation,
  getAssociationAdvisors,
  removeAdvisorFromAssociation,
} from "../../services/associationService";
import {
  AdvisorAvailability,
  AdvisorProfileResponse,
} from "../../types/advisorProfile.types";
import { ROSTER_PAGE_SIZE } from "../../types/associationRoster.types";
import {
  isAdvisorNotInAssociation,
  isAdvisorProfileGone,
  mapAddAdvisorError,
  mapRemoveAdvisorError,
} from "../../utils/associationRosterErrors";
import { shouldRefetchAssociationsAfterError } from "../../utils/associationErrors";
import {
  LOAD_ERROR_COPY,
  NO_ASSOCIATION_COPY,
  NO_ASSOCIATION_TITLE,
  REFRESH_ERROR_COPY,
} from "../AssociationProfilePage/AssociationProfilePage";
import styles from "./AssociationAdvisorsPage.module.scss";

// Association portal, `/association/advisors`: the advisor roster of the
// selected association. Add an existing advisor by the e-mail they registered
// with, list with a debounced name search, an "only available" filter and
// "Učitaj još" paging, remove behind a confirm. Every request belongs to the
// association it was sent for: a read is aborted when the association or a
// filter changes, a write's answer is dropped when the association changed
// meanwhile.

export const PAGE_TITLE = "Savetnici udruženja";
export const EMAIL_LABEL = "E-mail savetnika";
export const ADD_BUTTON_LABEL = "Dodaj savetnika";
export const INVALID_EMAIL_COPY = "Unesite ispravnu e-mail adresu.";
export const ADDED_NOTICE = "Savetnik je dodat.";
export const REMOVED_NOTICE = "Savetnik je uklonjen.";
export const SEARCH_PLACEHOLDER = "Pretraga po imenu";
export const ONLY_AVAILABLE_LABEL = "Samo dostupni";
export const LOAD_MORE_LABEL = "Učitaj još";
export const EMPTY_ROSTER_COPY =
  "Udruženje još nema savetnike. Dodajte savetnika putem e-mail adrese kojom se registrovao na dobrocudan.rs.";
export const NO_MATCH_COPY = "Nijedan savetnik ne odgovara pretrazi.";
export const REMOVE_NOTE =
  "Profil savetnika i njegova druga udruženja ostaju netaknuti.";
export const SEARCH_DEBOUNCE_MS = 300;

export const AVAILABILITY_LABEL: Record<AdvisorAvailability, string> = {
  FREE: "Dostupan",
  IN_CALL: "U pozivu",
  UNAVAILABLE: "Nedostupan",
};
/** `availability` absent: the advisor never enrolled for online consultations. */
export const NOT_ENROLLED_LABEL = "Nije aktiviran";

const CDN_BASE = process.env.REACT_APP_CDN_PATH ?? "";
const PLACEHOLDER_PHOTO = "/profile.png";
const DIALOG_TITLE_ID = "association-advisor-remove-title";
const EMAIL_INPUT_ID = "association-advisor-email";
const EMAIL_ERROR_ID = "association-advisor-email-error";

/** Shape check only; the server decides whether the account exists. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isEmailShaped = (value: string): boolean => EMAIL_SHAPE.test(value);

export const advisorFullName = (advisor: AdvisorProfileResponse): string =>
  [advisor.firstName, advisor.secondName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ") || "Savetnik bez imena";

/** First small image, else the first image, else the generic avatar. */
export const advisorPhotoUrl = (advisor: AdvisorProfileResponse): string => {
  const photos = advisor.images.filter((file) => file.type === "IMAGE");
  const photo = photos.find((file) => file.isSmall === 1) ?? photos[0];
  return photo ? `${CDN_BASE}${photo.location}` : PLACEHOLDER_PHOTO;
};

interface RosterState {
  /** The association these rows belong to; rows of another one are never shown. */
  associationId: number | null;
  advisors: AdvisorProfileResponse[];
  /** 0-based page of the last loaded page. */
  pageNum: number;
  totalPages: number;
  /** "error" only when the first page of this association never arrived. */
  status: "loading" | "loaded" | "error";
  /** A refetch or "Učitaj još" failed; the rows shown are the last loaded. */
  refreshFailed: boolean;
}

const INITIAL_ROSTER: RosterState = {
  associationId: null,
  advisors: [],
  pageNum: 0,
  totalPages: 0,
  status: "loading",
  refreshFailed: false,
};

const AssociationAdvisorsPage: React.FC = () => {
  const dispatch = useDispatch();
  const userId = useSelector(selectTokenUserId);
  const { status, loadError } = useSelector(selectAssociationContextState);
  const association = useSelector(selectSelectedAssociation);
  const associationId = association?.id ?? null;

  const [roster, setRoster] = useState<RosterState>(INITIAL_ROSTER);
  const [fetching, setFetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(false);

  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [pendingRemoval, setPendingRemoval] =
    useState<AdvisorProfileResponse | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const loadMoreControllerRef = useRef<AbortController | null>(null);
  // Writes are not aborted (the server may already have applied them); their
  // answers are dropped instead when the page is gone or shows another
  // association.
  const mountedRef = useRef(true);
  const associationIdRef = useRef<number | null>(associationId);
  associationIdRef.current = associationId;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /** The write's answer still belongs on screen. */
  const stillFor = (target: number) =>
    mountedRef.current && associationIdRef.current === target;

  /** Re-read `/mine` so the advisor count and the administered set stay true. */
  const refreshAssociations = useCallback(() => {
    if (userId != null) {
      dispatch<any>(fetchMyAssociations(userId));
    }
  }, [dispatch, userId]);

  // Feedback of one association never carries over to the next.
  useEffect(() => {
    setAddError(null);
    setNotice(null);
    setPendingRemoval(null);
    setRemoveError(null);
  }, [associationId]);

  useEffect(() => {
    const handle = setTimeout(
      () => setSearch(searchInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [searchInput]);

  // The first page, for the selected association and the current filters.
  useEffect(() => {
    if (associationId == null) {
      return;
    }
    const controller = new AbortController();
    loadMoreControllerRef.current?.abort();
    setLoadingMore(false);
    setFetching(true);
    getAssociationAdvisors(
      associationId,
      {
        search: search || undefined,
        onlyAvailable,
        page: 0,
        numOfReturns: ROSTER_PAGE_SIZE,
      },
      controller.signal,
    )
      .then((page) => {
        if (controller.signal.aborted) {
          return;
        }
        setRoster({
          associationId,
          advisors: page.elements,
          pageNum: page.pageNum,
          totalPages: page.totalNumOfPages,
          status: "loaded",
          refreshFailed: false,
        });
        setFetching(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        console.error("AssociationAdvisorsPage: roster fetch failed", error);
        setRoster((prev) =>
          prev.associationId === associationId && prev.status === "loaded"
            ? { ...prev, refreshFailed: true }
            : { ...INITIAL_ROSTER, associationId, status: "error" },
        );
        setFetching(false);
      });
    return () => {
      controller.abort();
      loadMoreControllerRef.current?.abort();
    };
  }, [associationId, search, onlyAvailable, reloadKey]);

  const retry = () => {
    if (userId != null) {
      dispatch<any>(fetchMyAssociations(userId));
    }
  };

  if (status === "idle" || status === "loading") {
    return (
      <div className={styles.page}>
        <p className={styles.placeholder}>Učitavanje udruženja...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p role="alert" className={styles.error}>
            {LOAD_ERROR_COPY}
          </p>
          <Button buttonType="blue" onClick={retry}>
            Pokušaj ponovo
          </Button>
        </div>
      </div>
    );
  }

  if (association == null || associationId == null) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.pageTitle}>{NO_ASSOCIATION_TITLE}</h1>
          <p className={styles.copy}>{NO_ASSOCIATION_COPY}</p>
          <Link className={styles.link} to="/association">
            Nazad na udruženje
          </Link>
        </div>
      </div>
    );
  }

  const current = roster.associationId === associationId ? roster : null;
  const hasMore = current != null && current.pageNum + 1 < current.totalPages;
  const filtered = search !== "" || onlyAvailable;

  const submitAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    if (adding) {
      return;
    }
    const value = email.trim();
    if (!isEmailShaped(value)) {
      setAddError(INVALID_EMAIL_COPY);
      return;
    }
    const target = associationId;
    setAdding(true);
    setAddError(null);
    setNotice(null);
    try {
      const added = await addAdvisorToAssociation(target, value);
      if (!stillFor(target)) {
        return;
      }
      setEmail("");
      setNotice(ADDED_NOTICE);
      setRoster((prev) =>
        prev.associationId === target && prev.status === "loaded"
          ? {
              ...prev,
              advisors: [
                added,
                ...prev.advisors.filter((a) => a.id !== added.id),
              ],
            }
          : prev,
      );
      if (current == null || current.status !== "loaded") {
        // The first page is not here yet: re-read it instead of guessing.
        setReloadKey((key) => key + 1);
      }
      refreshAssociations();
    } catch (error) {
      if (!stillFor(target)) {
        return;
      }
      setAddError(mapAddAdvisorError(error));
      // The pairing is gone, or the association is (a 404 after another
      // administrator deleted it): the administered set is one re-read away.
      if (shouldRefetchAssociationsAfterError(error)) {
        refreshAssociations();
      }
    } finally {
      if (mountedRef.current) {
        setAdding(false);
      }
    }
  };

  const loadMore = () => {
    if (current == null || !hasMore || loadingMore || fetching) {
      return;
    }
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    setLoadingMore(true);
    getAssociationAdvisors(
      associationId,
      {
        search: search || undefined,
        onlyAvailable,
        page: current.pageNum + 1,
        numOfReturns: ROSTER_PAGE_SIZE,
      },
      controller.signal,
    )
      .then((page) => {
        if (controller.signal.aborted) {
          return;
        }
        setRoster((prev) => {
          // A row added on this page may also sit on a later server page.
          const known = new Set(prev.advisors.map((a) => a.id));
          return {
            ...prev,
            advisors: [
              ...prev.advisors,
              ...page.elements.filter((a) => !known.has(a.id)),
            ],
            pageNum: page.pageNum,
            totalPages: page.totalNumOfPages,
            refreshFailed: false,
          };
        });
        setLoadingMore(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        console.error("AssociationAdvisorsPage: next page failed", error);
        setRoster((prev) => ({ ...prev, refreshFailed: true }));
        setLoadingMore(false);
      });
  };

  const closeRemoval = () => {
    if (removing) {
      return;
    }
    setPendingRemoval(null);
    setRemoveError(null);
  };

  const confirmRemoval = async () => {
    if (pendingRemoval == null || removing) {
      return;
    }
    const target = associationId;
    const advisor = pendingRemoval;
    setRemoving(true);
    setRemoveError(null);
    setNotice(null);
    try {
      await removeAdvisorFromAssociation(target, advisor.id);
      if (!stillFor(target)) {
        return;
      }
      setRoster((prev) =>
        prev.associationId === target
          ? {
              ...prev,
              advisors: prev.advisors.filter((a) => a.id !== advisor.id),
            }
          : prev,
      );
      setPendingRemoval(null);
      setNotice(REMOVED_NOTICE);
      refreshAssociations();
    } catch (error) {
      if (!stillFor(target)) {
        return;
      }
      if (isAdvisorNotInAssociation(error) || isAdvisorProfileGone(error)) {
        // Already gone from this association (or the profile was deleted):
        // the truth is one re-read away.
        setPendingRemoval(null);
        setReloadKey((key) => key + 1);
        refreshAssociations();
        return;
      }
      setRemoveError(mapRemoveAdvisorError(error));
      if (shouldRefetchAssociationsAfterError(error)) {
        refreshAssociations();
      }
    } finally {
      if (mountedRef.current) {
        setRemoving(false);
      }
    }
  };

  const retryRoster = (
    <Button buttonType="white" onClick={() => setReloadKey((key) => key + 1)}>
      Pokušaj ponovo
    </Button>
  );

  const renderRoster = () => {
    if (current == null || current.status === "loading") {
      return <p className={styles.placeholder}>Učitavanje savetnika...</p>;
    }
    if (current.status === "error") {
      return (
        <div className={styles.inlineError}>
          <p role="alert" className={styles.error}>
            {LOAD_ERROR_COPY}
          </p>
          {retryRoster}
        </div>
      );
    }
    return (
      <>
        {current.refreshFailed && (
          <div className={styles.inlineError}>
            <p role="alert" className={styles.error}>
              {LOAD_ERROR_COPY}
            </p>
            {retryRoster}
          </div>
        )}
        {current.advisors.length === 0 ? (
          <p className={styles.copy}>
            {filtered ? NO_MATCH_COPY : EMPTY_ROSTER_COPY}
          </p>
        ) : (
          <ul className={styles.list} aria-busy={fetching}>
            {current.advisors.map((advisor) => {
              const name = advisorFullName(advisor);
              const areas = advisor.advisoryAreas
                .map((area) => area.name)
                .join(", ");
              const availability = advisor.availability;
              return (
                <li key={advisor.id} className={styles.row}>
                  <img
                    className={styles.photo}
                    src={advisorPhotoUrl(advisor)}
                    alt=""
                  />
                  <div className={styles.info}>
                    <span className={styles.name}>{name}</span>
                    {advisor.city && (
                      <span className={styles.detail}>{advisor.city}</span>
                    )}
                    {areas && <span className={styles.detail}>{areas}</span>}
                    {advisor.numOfRates != null &&
                      advisor.numOfRates > 0 &&
                      advisor.averageRate != null && (
                        <span className={styles.detail}>
                          {`Ocena ${advisor.averageRate.toFixed(1)} (${
                            advisor.numOfRates
                          })`}
                        </span>
                      )}
                  </div>
                  <div className={styles.rowActions}>
                    <span
                      className={
                        availability
                          ? styles[`availability_${availability}`]
                          : styles.availability_NONE
                      }
                    >
                      {availability
                        ? AVAILABILITY_LABEL[availability]
                        : NOT_ENROLLED_LABEL}
                    </span>
                    <Button
                      buttonType="white"
                      aria-label={`Ukloni savetnika ${name}`}
                      disabled={removing}
                      onClick={() => {
                        setRemoveError(null);
                        setPendingRemoval(advisor);
                      }}
                    >
                      Ukloni
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {hasMore && (
          <Button
            buttonType="white"
            className={styles.loadMore}
            disabled={loadingMore || fetching}
            onClick={loadMore}
          >
            {LOAD_MORE_LABEL}
          </Button>
        )}
      </>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h1 className={styles.pageTitle}>{PAGE_TITLE}</h1>
          <p className={styles.subtitle}>{association.name}</p>
        </div>
        <AssociationSwitcher disabled={adding || removing} />
      </header>
      {loadError && (
        <p role="alert" className={styles.notice}>
          {REFRESH_ERROR_COPY}
        </p>
      )}

      <section className={styles.card} aria-label="Dodavanje savetnika">
        <form className={styles.addForm} onSubmit={submitAdd} noValidate>
          <label className={styles.label} htmlFor={EMAIL_INPUT_ID}>
            {EMAIL_LABEL}
          </label>
          <div className={styles.addRow}>
            <input
              id={EMAIL_INPUT_ID}
              className={styles.input}
              type="email"
              autoComplete="off"
              value={email}
              readOnly={adding}
              aria-invalid={addError != null}
              aria-describedby={addError != null ? EMAIL_ERROR_ID : undefined}
              onChange={(event) => {
                setEmail(event.target.value);
                setAddError(null);
              }}
            />
            <Button buttonType="blue" type="submit" disabled={adding}>
              {ADD_BUTTON_LABEL}
            </Button>
          </div>
          {addError != null && (
            <p id={EMAIL_ERROR_ID} role="alert" className={styles.error}>
              {addError}
            </p>
          )}
        </form>
        {notice != null && (
          <p role="status" className={styles.success}>
            {notice}
          </p>
        )}
      </section>

      <section className={styles.card} aria-label="Savetnici">
        <div className={styles.filters}>
          <input
            className={styles.input}
            type="search"
            placeholder={SEARCH_PLACEHOLDER}
            aria-label={SEARCH_PLACEHOLDER}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={onlyAvailable}
              onChange={(event) => setOnlyAvailable(event.target.checked)}
            />
            {ONLY_AVAILABLE_LABEL}
          </label>
        </div>
        {renderRoster()}
      </section>

      <AccessibleDialog
        isOpen={pendingRemoval != null}
        onRequestClose={closeRemoval}
        titleId={DIALOG_TITLE_ID}
        initialFocusRef={cancelButtonRef}
        maxWidth={480}
      >
        {pendingRemoval != null && (
          <div className={styles.dialog}>
            <h3 id={DIALOG_TITLE_ID} className={styles.dialogTitle}>
              {`Ukloniti savetnika ${advisorFullName(
                pendingRemoval,
              )} iz udruženja?`}
            </h3>
            <p className={styles.copy}>{REMOVE_NOTE}</p>
            {removeError != null && (
              <p role="alert" className={styles.error}>
                {removeError}
              </p>
            )}
            <div className={styles.dialogActions}>
              <button
                ref={cancelButtonRef}
                type="button"
                className={styles.cancelButton}
                disabled={removing}
                onClick={closeRemoval}
              >
                Odustani
              </button>
              <Button
                buttonType="red"
                disabled={removing}
                onClick={confirmRemoval}
              >
                Ukloni
              </Button>
            </div>
          </div>
        )}
      </AccessibleDialog>
    </div>
  );
};

export default AssociationAdvisorsPage;
